import React, { useEffect, useRef, useState } from 'react';
import Lottie from 'lottie-react';
import { animated, useSpring } from '@react-spring/web';

interface WarningModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  message: string;
}

const WarningModal: React.FC<WarningModalProps> = ({
  isOpen,
  onClose,
  title,
  message,
}) => {
  const modal = useRef<HTMLDivElement>(null);
  const [animationData, setAnimationData] = useState<any>(null);

  // Cargar animación solo si está abierto
  useEffect(() => {
    if (isOpen) {
      import('../../assets/animations/alerts/alerta-warning.json').then(
        (data) => setAnimationData(data.default)
      );
    } else {
      setAnimationData(null);
    }
  }, [isOpen]);

  // Cierre por clic fuera
  useEffect(() => {
    const clickHandler = (event: MouseEvent) => {
      if (modal.current && !modal.current.contains(event.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', clickHandler);
    return () => document.removeEventListener('mousedown', clickHandler);
  }, [onClose]);

  // Cierre con ESC o Enter
  useEffect(() => {
    const keyHandler = ({ key }: KeyboardEvent) => {
      if (key === 'Escape' || key === 'Enter') {
        onClose();
      }
    };
    document.addEventListener('keydown', keyHandler);
    return () => document.removeEventListener('keydown', keyHandler);
  }, [onClose]);

  const animationStyle = useSpring({
    opacity: isOpen ? 1 : 0,
    config: { duration: 300 },
  });

  if (!isOpen) return null;

  return (
    <div className="fixed left-0 top-0 z-[999999] flex h-full min-h-screen w-full items-center justify-center bg-black/90 px-4 py-5">
      <animated.div
        style={animationStyle}
        ref={modal}
        className="w-full max-w-md rounded-lg bg-white px-8 py-12 text-center shadow-lg dark:bg-boxdark"
      >
        {/* 🟠 Animación de advertencia */}
        {animationData && (
          <div className="mb-4 flex justify-center">
            <Lottie
              animationData={animationData}
              loop={true}
              style={{ width: 80, height: 80 }}
            />
          </div>
        )}

        {/* Título */}
        <h3 className="pb-2 text-xl font-bold text-[#D97706] dark:text-yellow-400 sm:text-2xl">
          {title}
        </h3>

        {/* Mensaje */}
        <div className="text-left text-gray-800 dark:text-gray-300 mb-6 whitespace-pre-line">
          {message}
        </div>

        {/* Botón */}
        <button
          onClick={onClose}
          className="block w-full rounded border border-[#F9C107] bg-[#F9C107] p-3 text-center font-medium text-white transition hover:bg-opacity-90"
        >
          Entendido
        </button>
      </animated.div>
    </div>
  );
};

export default React.memo(WarningModal);
